import { Dialog, useDialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';

import { hasItems } from 'src/utils/hasItems';

import { CoreGlobalNavigationHeaderData } from 'src/types/components';

import CloseIcon from 'assets/icons/close.svg';
import Facebook from 'assets/icons/facebook.svg';
import Instagram from 'assets/icons/instagram.svg';
import Linkedin from 'assets/icons/linkedin.svg';
import MenuIcon from 'assets/icons/menu.svg';
import TwitterX from 'assets/icons/twitter-x.svg';
import YouTube from 'assets/icons/youtube.svg';

import ArrowLink from 'components/BaseHelpers/ArrowLink';
import Link from 'components/BaseHelpers/Link';
import PatternBackground from 'components/BaseHelpers/PatternBackground';
import Text from 'components/BaseHelpers/Text';

import styles from './MegaMenu.module.scss';

export type MegaMenuProps = Pick<
  CoreGlobalNavigationHeaderData['fields'],
  'primaryNav' | 'ancillaryNav' | 'socialMedia'
> & {
  className?: string;
};

const MegaMenu = (props: MegaMenuProps): JSX.Element => {
  const { primaryNav, ancillaryNav, socialMedia, className } = props;

  const dialog = useDialog({ restoreFocus: true, modal: false, trapFocus: true });

  const { menuLabel } = useDictionary();
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = () => dialog.setOpen(false);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => router.events.off('routeChangeStart', handleRouteChange);
  }, [router]);

  return (
    <div className={cn(styles.main, className)}>
      <Dialog.RootProvider value={dialog}>
        <Dialog.Trigger className={styles.trigger}>
          <span className={cn(styles['menu-button-label'], 'body-sm')}>{menuLabel}</span>
          <span className={styles['menu-icon']}>
            {dialog.open ? (
              <CloseIcon className={styles['menu-close-icon']} />
            ) : (
              <MenuIcon className={styles['menu-open-icon']} />
            )}
          </span>
        </Dialog.Trigger>
        <Portal>
          <Dialog.Backdrop className={styles.backdrop}>
            <PatternBackground className={styles['backdrop-pattern']} size="large" theme="dark" />
          </Dialog.Backdrop>
          <Dialog.Positioner className={styles.positioner}>
            <Dialog.Content className={styles.content}>
              <div className={cn(styles.grid, 'container-10')}>
                <div className={styles.left}>
                  <ul className={styles.primary}>
                    {primaryNav.map((primary, index) => (
                      <li key={index}>
                        <ArrowLink
                          variant="large"
                          className={styles['primary-link']}
                          href={primary.href}
                          openNewTab={primary.target === '_blank'}
                          field={primary.label}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.right}>
                  <ul className={styles.ancillary}>
                    {ancillaryNav.map((ancillary, index) => (
                      <li key={index}>
                        <ArrowLink
                          variant="small"
                          className={styles['ancillary-link']}
                          href={ancillary.href}
                          openNewTab={ancillary.target === '_blank'}
                          field={ancillary.label}
                        />
                      </li>
                    ))}
                  </ul>
                  {hasItems(socialMedia.links) && (
                    <div className={styles['social-media']}>
                      <Text className={styles['social-label']} field={socialMedia.label} />
                      <ul className={styles['social-links']}>
                        {socialMedia.links.map((link, index) => (
                          <li key={index}>
                            <Link
                              href={link.href}
                              openNewTab
                              className={styles['social-link']}
                              aria-label={link.platform}
                            >
                              {link.platform === 'facebook' ? (
                                <Facebook className={cn(styles['social-icon'], styles.facebook)} />
                              ) : link.platform === 'x' ? (
                                <TwitterX className={cn(styles['social-icon'], styles.x)} />
                              ) : link.platform === 'instagram' ? (
                                <Instagram
                                  className={cn(styles['social-icon'], styles.instagram)}
                                />
                              ) : link.platform === 'linkedin' ? (
                                <Linkedin className={cn(styles['social-icon'], styles.linkedin)} />
                              ) : link.platform === 'youtube' ? (
                                <YouTube className={cn(styles['social-icon'], styles.youtube)} />
                              ) : null}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.RootProvider>
    </div>
  );
};

export default MegaMenu;
